import React, { useEffect, useState, useCallback } from 'react';
import {
  Bell,
  Search,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Code2,
  RefreshCw,
  Tag,
  Radio,
  Clock,
} from 'lucide-react';
import {
  fetchUserNotifications,
  fetchVendorNotifications,
  RecipientNotificationItem,
  RecipientNotificationResponse,
} from '../../services/MasterAdmin/masterAdminNotificationService';

interface RecipientNotificationsListProps {
  recipientType: 'user' | 'vendor';
  recipientId: string;
  recipientName?: string;
}

export const RecipientNotificationsList: React.FC<RecipientNotificationsListProps> = ({
  recipientType,
  recipientId,
  recipientName,
}) => {
  const [notifications, setNotifications] = useState<RecipientNotificationItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(20);
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedPayloads, setExpandedPayloads] = useState<Record<string, boolean>>({});
  const [pagination, setPagination] = useState<{
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  }>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const loadNotifications = useCallback(async () => {
    if (!recipientId) return;
    try {
      setLoading(true);
      setError(null);
      let res: RecipientNotificationResponse;
      if (recipientType === 'user') {
        res = await fetchUserNotifications(recipientId, { page, limit, unreadOnly });
      } else {
        res = await fetchVendorNotifications(recipientId, { page, limit, unreadOnly });
      }

      if (res.success && res.data) {
        setNotifications(res.data.notifications || []);
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
      } else {
        setError('Failed to load notification history.');
      }
    } catch (err: any) {
      console.error('Error fetching recipient notifications:', err);
      setError(err?.message || 'Error loading notification list.');
    } finally {
      setLoading(false);
    }
  }, [recipientType, recipientId, page, limit, unreadOnly]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const togglePayloadExpand = (id: string) => {
    setExpandedPayloads((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return dateString;
    return d.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const filteredNotifications = notifications.filter((item) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const titleMatch = (item.title || '').toLowerCase().includes(query);
    const bodyMatch = (item.body || '').toLowerCase().includes(query);
    const typeMatch = (item.type || '').toLowerCase().includes(query);
    return titleMatch || bodyMatch || typeMatch;
  });

  return (
    <div className="space-y-4">
      {/* Top summary and filter bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
            <Bell className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
              {recipientType === 'user' ? 'User Push Notifications' : 'Vendor Push Notifications'}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Total sent &amp; attempted: <span className="font-semibold text-gray-700 dark:text-gray-200">{pagination.totalItems}</span>
              {recipientName ? ` • ${recipientName}` : ''}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <button
            type="button"
            onClick={() => {
              setUnreadOnly((prev) => !prev);
              setPage(1);
            }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1.5 ${
              unreadOnly
                ? 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-300'
                : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {unreadOnly ? 'Unread only' : 'All notifications'}
          </button>

          <button
            type="button"
            onClick={loadNotifications}
            disabled={loading}
            className="p-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Notifications Content */}
      {loading ? (
        <div className="py-12 flex flex-col items-center justify-center text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
          <RefreshCw className="w-6 h-6 animate-spin text-primary-500 mb-2" />
          <span>Loading notifications list...</span>
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={loadNotifications} className="text-xs font-semibold underline">
            Retry
          </button>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="py-12 px-4 text-center rounded-xl bg-white dark:bg-gray-800 border border-dashed border-gray-200 dark:border-gray-700">
          <Bell className="w-8 h-8 text-gray-400 mx-auto mb-2 opacity-50" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            No notifications found
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {searchQuery
              ? 'Try modifying your search query.'
              : unreadOnly
              ? 'No unread notifications exist for this recipient.'
              : 'No notifications have been sent or attempted for this ID yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((item) => {
            const isExpanded = !!expandedPayloads[item.notificationId];
            const hasDataPayload = item.data && Object.keys(item.data).length > 0;

            return (
              <div
                key={item.notificationId}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 mt-0.5 shrink-0">
                      <Bell className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {item.title || 'Untitled Notification'}
                        </h5>

                        {/* Status badges */}
                        {item.isRead ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800">
                            <CheckCircle2 className="w-3 h-3" />
                            Read
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200/60 dark:border-blue-800">
                            Unread
                          </span>
                        )}

                        {item.type && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                            <Tag className="w-2.5 h-2.5" />
                            {item.type}
                          </span>
                        )}

                        {(item.broadcastToAllUsers || item.broadcastToAllVendors) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                            <Radio className="w-3 h-3" />
                            Broadcast Campaign
                          </span>
                        )}
                      </div>

                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                        {item.body}
                      </p>

                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(item.createdAt)}
                        </span>
                        <span className="font-mono text-[10px] text-gray-400 dark:text-gray-500">
                          ID: {item.notificationId}
                        </span>
                      </div>
                    </div>
                  </div>

                  {hasDataPayload && (
                    <button
                      type="button"
                      onClick={() => togglePayloadExpand(item.notificationId)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
                    >
                      <Code2 className="w-3.5 h-3.5 text-gray-500" />
                      <span>{isExpanded ? 'Hide Data' : 'Data Payload'}</span>
                    </button>
                  )}
                </div>

                {/* Expanded JSON Data Payload */}
                {isExpanded && hasDataPayload && (
                  <div className="mt-3 p-3 rounded-lg bg-gray-900 text-gray-100 text-xs font-mono overflow-x-auto border border-gray-800">
                    <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-gray-800 text-[10px] text-gray-400">
                      <span>Data Payload (JSON)</span>
                      <span>Keys: {Object.keys(item.data || {}).length}</span>
                    </div>
                    <pre className="text-[11px] leading-snug">
                      {JSON.stringify(item.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Bar */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 px-1 text-xs text-gray-500 dark:text-gray-400">
          <span>
            Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!pagination.hasPreviousPage || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Previous
            </button>

            <button
              type="button"
              disabled={!pagination.hasNextPage || loading}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
